import hostyAppIcon from '@assets/images/hosty-app-icon.png';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigationType } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePanelMenu } from '../../hooks/usePanelMenu';
import { LEGAL_URLS } from '../../config/legal';
import { CONTACT_EMAIL, SOCIAL_URLS } from '../../config/contact';
import { WhatsAppFloatingButton } from './WhatsAppFloatingButton';
import { SunmiAnnouncementBanner } from './SunmiAnnouncementBanner';
import './layouts.css';

export function MarketingLayout() {
  const { isAuthenticated } = useAuth();
  const { open, toggle, close } = usePanelMenu();
  const menuRef = useRef<HTMLDialogElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);
  const announcementRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousPath = useRef(location.pathname);

  useLayoutEffect(() => {
    const layout = layoutRef.current;
    const announcement = announcementRef.current;
    const header = headerRef.current;
    if (!layout || !announcement || !header) return;

    const updateChromeHeight = () => {
      const height = announcement.getBoundingClientRect().height + header.getBoundingClientRect().height;
      layout.style.setProperty('--marketing-chrome-height', `${height}px`);
    };
    updateChromeHeight();
    // The wrapper remains observable when the dismissible announcement disappears.
    const observer = new ResizeObserver(updateChromeHeight);
    observer.observe(announcement);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    // Preserve browser history restoration; new pages start at their heading or anchor.
    if (navigationType === 'POP') return;
    const frame = requestAnimationFrame(() => {
      const target = location.hash ? document.getElementById(location.hash.slice(1)) : null;
      if (target) target.scrollIntoView();
      else window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      mainRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, location.hash, navigationType]);

  useEffect(() => {
    const menu = menuRef.current;
    if (open && !menu?.open) menu?.showModal();
    if (!open && menu?.open) menu.close();
  }, [open]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 961px)');
    const closeOnDesktop = () => { if (desktop.matches) close(); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, [close]);

  return (
    <div ref={layoutRef} className="marketing-layout">
      <a className="marketing-skip-link" href="#main-content">Saltar al contenido</a>
      <div ref={announcementRef}>
        <SunmiAnnouncementBanner />
      </div>

      <header ref={headerRef} className="site-header">
        <div className="container site-header__inner">
          <Link to="/" className="brand">
            <img src={hostyAppIcon} alt="" className="brand__mark" width={32} height={32} />
            <span>
              TPV <strong>Hosty</strong>
            </span>
          </Link>
          <nav className="site-nav" aria-label="Navegación principal">
            <NavLink to="/caracteristicas">Características</NavLink>
            <NavLink to="/precios">Precios</NavLink>
            <NavLink to="/descargar">Descargar</NavLink>
            <NavLink to="/contacto">Contacto</NavLink>
          </nav>
          <div className="site-header__actions">
            {isAuthenticated ? (
              <Link to="/panel" className="ui-btn ui-btn--primary ui-btn--sm">
                Ir al panel
              </Link>
            ) : (
              <>
                <Link to="/login" className="site-link">
                  Iniciar sesión
                </Link>
                <Link to="/registro" className="ui-btn ui-btn--primary ui-btn--sm">
                  Crear cuenta
                </Link>
              </>
            )}
          </div>
          <button
            type="button"
            className="site-menu-toggle"
            onClick={toggle}
            aria-expanded={open}
            aria-controls="marketing-mobile-menu"
            aria-haspopup="dialog"
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
          >
            <span className="site-menu-toggle__icon" aria-hidden="true" />
          </button>
        </div>
      </header>

      <dialog
        ref={menuRef}
        id="marketing-mobile-menu"
        className="site-mobile-menu"
        aria-label="Menú principal"
        onCancel={(event) => { event.preventDefault(); close(); }}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const controls = event.currentTarget.querySelectorAll<HTMLElement>('button, a[href]');
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
        onClick={(event) => { if (event.target === event.currentTarget) close(); }}
      >
      <nav className="site-mobile-nav is-open" aria-label="Navegación móvil">
        <button type="button" className="ui-btn ui-btn--secondary site-mobile-menu__close" onClick={close}>Cerrar menú</button>
        <NavLink to="/caracteristicas">Características</NavLink>
        <NavLink to="/precios">Precios</NavLink>
        <NavLink to="/descargar">Descargar</NavLink>
        <NavLink to="/contacto">Contacto</NavLink>
        <div className="site-mobile-nav__actions">
          {isAuthenticated ? (
            <Link to="/panel" className="ui-btn ui-btn--primary ui-btn--sm">
              Ir al panel
            </Link>
          ) : (
            <>
              <Link to="/login" className="site-link">
                Iniciar sesión
              </Link>
              <Link to="/registro" className="ui-btn ui-btn--primary ui-btn--sm">
                Crear cuenta
              </Link>
            </>
          )}
        </div>
      </nav>
      </dialog>

      <main ref={mainRef} id="main-content" tabIndex={-1}>
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="container site-footer__top">
          <div className="site-footer__brand">
            <Link to="/" className="brand">
              <img src={hostyAppIcon} alt="" className="brand__mark" width={28} height={28} />
              <span>
                TPV <strong>Hosty</strong>
              </span>
            </Link>
            <p>
              Sala, cocina y caja conectadas. Software TPV para bares, restaurantes y cafeterías.
            </p>
            <div className="site-footer__social">
              <a
                href={SOCIAL_URLS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram de TPV Hosty"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
                  <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.8" />
                  <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" />
                </svg>
              </a>
              <a
                href={SOCIAL_URLS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook de TPV Hosty"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path
                    d="M14.5 8.5h2V5.2c-.35-.05-1.54-.15-2.93-.15-2.9 0-4.89 1.77-4.89 5.02v2.68H5.75v3.7h2.93V21h3.82v-6.55h2.82l.45-3.7h-3.27V10.4c0-1.07.29-1.9 1.99-1.9Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
              <a
                href={SOCIAL_URLS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn de TPV Hosty"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path
                    d="M6.94 8.5H4V20h2.94V8.5ZM5.47 4a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4ZM20 13.3c0-3.02-1.61-4.43-3.76-4.43-1.73 0-2.5.95-2.94 1.62V8.5H10.36c.04.85 0 12 0 12h2.94v-6.7c0-.36.03-.72.13-.98.29-.72.96-1.47 2.07-1.47 1.46 0 2.06 1.11 2.06 2.75V20H20v-6.7Z"
                    fill="currentColor"
                  />
                </svg>
              </a>
            </div>
          </div>

          <div className="site-footer__col">
            <h2>Producto</h2>
            <ul>
              <li><Link to="/caracteristicas">Características</Link></li>
              <li><Link to="/precios">Precios</Link></li>
              <li><Link to="/descargar">Descargar TPV</Link></li>
              <li><Link to="/contacto">Contacto</Link></li>
            </ul>
          </div>

          <div className="site-footer__col">
            <h2>Cuenta</h2>
            <ul>
              <li><Link to="/login">Iniciar sesión</Link></li>
              <li><Link to="/registro">Crear cuenta</Link></li>
              <li><Link to="/panel/licencias">Mis licencias</Link></li>
              <li><Link to="/panel/informes">Informes</Link></li>
            </ul>
          </div>

          <div className="site-footer__col">
            <h2>Legal</h2>
            <ul>
              <li><Link to={LEGAL_URLS.privacy}>Privacidad</Link></li>
              <li><Link to={LEGAL_URLS.terms}>Condiciones del servicio</Link></li>
              <li><Link to={LEGAL_URLS.dataDeletion}>Eliminación de datos</Link></li>
            </ul>
          </div>

          <div className="site-footer__col">
            <h2>Soporte</h2>
            <ul>
              <li><Link to="/contacto">Contacto</Link></li>
              <li><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
              <li><Link to="/staff">Panel Staff</Link></li>
              <li><Link to="/erp">ERP (admin)</Link></li>
            </ul>
          </div>
        </div>

        <div className="site-footer__bottom">
          <div className="container site-footer__bottom-inner">
            <span>© {new Date().getFullYear()} TPV Hosty · tpvhosty.com</span>
            <span className="site-footer__made-in">
              <span className="site-footer__flag" aria-hidden="true">
                <svg viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
                  <rect width="3" height="0.5" fill="#AA151B" />
                  <rect y="0.5" width="3" height="1" fill="#F1BF00" />
                  <rect y="1.5" width="3" height="0.5" fill="#AA151B" />
                </svg>
              </span>
              Hecho para hostelería española
            </span>
          </div>
        </div>
      </footer>

      <WhatsAppFloatingButton />
    </div>
  );
}
